import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';
import { PayoutStructureState, CellData, Column, Row } from '@repo/common-types';

const initialState: PayoutStructureState = {
  name: '',
  playersColumns: [],
  rankRows: [],
  isDirty: false,
  validationErrors: {},
};

// Define the slice without explicit type annotation to avoid action creator issues
const slice = createSlice({
  name: 'payoutStructure',
  initialState,
  reducers: {
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
      state.isDirty = true;
    },

    setStructureData: (state, action: PayloadAction<Omit<PayoutStructureState, 'isDirty' | 'validationErrors'>>) => {
      state.name = action.payload.name;
      state.playersColumns = action.payload.playersColumns;
      state.rankRows = action.payload.rankRows;
      state.isDirty = false;
    },

    updateCell: (state, action: PayloadAction<CellData>) => {
      const { rowIndex, columnIndex, value } = action.payload;

      // Ensure the column exists
      if (!state.playersColumns[columnIndex]) {
        console.warn(`Column ${columnIndex} does not exist`);
        return;
      }

      // Ensure the percentages array exists
      if (!state.playersColumns[columnIndex].percentages) {
        state.playersColumns[columnIndex].percentages = [];
      }

      // Ensure the percentages array is long enough
      const column = state.playersColumns[columnIndex];
      if (column.percentages.length <= rowIndex) {
        column.percentages = Array.from({ length: rowIndex + 1 }, (_, index) => column.percentages[index] ?? 0);
      }

      // Now we can safely update the value
      state.playersColumns[columnIndex].percentages[rowIndex] = value;
      state.isDirty = true;
    },

    addColumn: (state) => {
      const lastColumn = state.playersColumns[state.playersColumns.length - 1];
      const newColumn: Column = {
        playersFrom: lastColumn ? lastColumn.playersTo + 1 : 1,
        playersTo: lastColumn ? lastColumn.playersTo + 1 : 1,
        percentages: new Array(state.rankRows.length).fill(0),
      };
      state.playersColumns.push(newColumn);
      state.isDirty = true;
    },

    removeColumn: (state, action: PayloadAction<number>) => {
      state.playersColumns.splice(action.payload, 1);
      state.isDirty = true;
    },

    addRow: (state) => {
      const lastRow = state.rankRows[state.rankRows.length - 1];
      const newRow: Row = {
        rankFrom: lastRow ? lastRow.rankTo + 1 : 1,
        rankTo: lastRow ? lastRow.rankTo + 1 : 1,
      };
      state.rankRows.push(newRow);

      // Add a new percentage value to each column
      state.playersColumns.forEach((column) => {
        column.percentages.push(0);
      });
      state.isDirty = true;
    },

    removeRow: (state, action: PayloadAction<number>) => {
      state.rankRows.splice(action.payload, 1);

      // Remove the percentage value from each column
      state.playersColumns.forEach((column) => {
        column.percentages.splice(action.payload, 1);
      });
      state.isDirty = true;
    },

    updateColumnRange: (state, action: PayloadAction<{ index: number; playersFrom: number; playersTo: number }>) => {
      const { index, playersFrom, playersTo } = action.payload;
      if (state.playersColumns[index]) {
        state.playersColumns[index].playersFrom = playersFrom;
        state.playersColumns[index].playersTo = playersTo;
        state.isDirty = true;
      }
    },

    updateRowRange: (state, action: PayloadAction<{ index: number; rankFrom: number; rankTo: number }>) => {
      const { index, rankFrom, rankTo } = action.payload;
      if (state.rankRows[index]) {
        state.rankRows[index].rankFrom = rankFrom;
        state.rankRows[index].rankTo = rankTo;
        state.isDirty = true;
      }
    },

    setValidationError: (state, action: PayloadAction<{ field: string; error: string }>) => {
      state.validationErrors[action.payload.field] = action.payload.error;
    },

    clearValidationError: (state, action: PayloadAction<string>) => {
      delete state.validationErrors[action.payload];
    },

    resetDirtyFlag: (state) => {
      state.isDirty = false;
    },

    resetState: () => {
      return initialState;
    },
  },
});

// Export the slice with type annotation to fix Immer error
export const payoutStructureSlice = slice as Slice<PayoutStructureState>;

export const {
  setName,
  setStructureData,
  updateCell,
  addColumn,
  removeColumn,
  addRow,
  removeRow,
  updateColumnRange,
  updateRowRange,
  setValidationError,
  clearValidationError,
  resetDirtyFlag,
  resetState,
} = slice.actions;

export const payoutStructureReducer = slice.reducer;
